import { v4 } from 'uuid';
import { WorkflowTab } from '../../../Api/Orders/ordersApi';
import { ScheduleSetting } from '../../../store/ScheduleSettings';
import { getCurrentPreferences, updatePreferencesKey } from '../../../store/User';
import { sameJson } from '../../../utils/sameJson';
import { WORKFLOW_TAB_ORDER } from '../../Pages/Orders/workflowTabs';

/** A report's schedule `view_key`, which is also the user customization key its tabs are stored under. */
export type OrdersReportKey = `order_grid_${string}`;

/** A schedule of an Orders report, whose `view_key` names where the report's tabs are stored. */
export type OrdersReport = ScheduleSetting & { view_key: OrdersReportKey };

/**
 * `tabs_not_stored`: a new report's tabs were refused, so its schedule was not sent.
 * `schedule_refused`: the schedule was refused; a new report's tabs were removed again, an edited one's left as they were.
 * `schedule_not_kept`: the schedule was stored, but not as sent; its tabs are kept, as the stored schedule reads them.
 * `schedule_saved_without_tabs`: an edited report's schedule was saved, but its changed tabs were refused.
 */
export type OrdersReportSaveOutcome =
  | 'saved'
  | 'tabs_not_stored'
  | 'schedule_refused'
  | 'schedule_not_kept'
  | 'schedule_saved_without_tabs';

// Names the change in the user's activity log, which would otherwise gain a new action per report key.
const PREFERENCES_CHANGE = 'Orders Report';

/** A key for a new report: an id, as names repeat and get edited, and the key is where the report's tabs are kept. */
export const newOrdersReportKey = (): OrdersReportKey => `order_grid_${v4()}`;

/** The tabs stored for a report, limited to the tabs the Orders page has and kept in its order. */
export const includedTabsOf = (key: OrdersReportKey): WorkflowTab[] => {
  const stored = getCurrentPreferences().content[key]?.included_stages ?? [];
  return WORKFLOW_TAB_ORDER.filter((tab) => stored.includes(tab));
};

const writeIncludedTabs = async (key: OrdersReportKey, tabs: WorkflowTab[] | null): Promise<boolean> => {
  const { data } = await updatePreferencesKey(key, tabs ? { included_stages: tabs } : undefined, PREFERENCES_CHANGE);
  return !!data;
};

export interface SaveOrdersReportParams {
  key: OrdersReportKey;
  tabs: WorkflowTab[];
  editing: boolean;
  // Calls `stored` once the API stored the schedule, as it answers null for one it stored but did not keep as sent.
  saveSchedule: (stored: () => void) => Promise<ScheduleSetting | null>;
}

/** Saves a report's schedule and the tabs it reads at every run, so that a refused schedule leaves the tabs as they were. */
export const saveOrdersReport = async ({
  key,
  tabs,
  editing,
  saveSchedule
}: SaveOrdersReportParams): Promise<OrdersReportSaveOutcome> => {
  let stored = false;
  const noteStored = () => {
    stored = true;
  };
  // An edited report's key still holds its tabs, so a refused schedule leaves nothing to put back.
  if (editing) {
    const saved = await saveSchedule(noteStored);
    if (!saved && !stored) return 'schedule_refused';
    const scheduleOutcome = saved ? 'saved' : 'schedule_not_kept';
    if (sameJson(includedTabsOf(key), tabs)) return scheduleOutcome;
    return (await writeIncludedTabs(key, tabs)) ? scheduleOutcome : 'schedule_saved_without_tabs';
  }
  // A new report's tabs go first: a schedule whose tabs are missing is accepted, but never sends.
  if (!(await writeIncludedTabs(key, tabs))) return 'tabs_not_stored';
  let saved: ScheduleSetting | null = null;
  try {
    saved = await saveSchedule(noteStored);
  } finally {
    // A failed removal leaves only a key no schedule reads, and must not replace what the schedule's save reported.
    if (!saved && !stored) await writeIncludedTabs(key, null).catch(() => false);
  }
  if (saved) return 'saved';
  return stored ? 'schedule_not_kept' : 'schedule_refused';
};
